import { ReactElement } from 'react';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import MarkdownCopy from '@components/cms/Slices/MarkdownCopy';
import Container from '@components/global/Container';
import SectionAccordion from '_components/UI/SectionAccordion';
import { SeoAccordionData } from '@adapters/prismic/utils/mapSeoAccordionData';

import styles from './SeoContentAccordion.module.scss';

export interface SeoContentAccordionProps {
  data?: SeoAccordionData | null;
}

// Render the heading markdown inline so it inherits the accordion header
// typography defined in SCSS. Unwrap block-level elements (paragraphs and any
// headings a `## Title` author might introduce) to keep the content inline and
// avoid nesting block tags inside the wrapping <h2>.
const unwrap = ({ children }: { children?: React.ReactNode }) => (
  <>{children}</>
);
const inlineMarkdownComponents = {
  p: unwrap,
  h1: unwrap,
  h2: unwrap,
  h3: unwrap,
  h4: unwrap,
  h5: unwrap,
  h6: unwrap,
};

const SeoContentAccordion = ({
  data,
}: SeoContentAccordionProps): ReactElement | null => {
  const items = data?.items ?? [];

  if (items.length === 0) {
    return null;
  }

  return (
    <section className={styles.container}>
      <Container>
        <div className={styles.accordions}>
          {items.map((item, index) => (
            <SectionAccordion
              key={`seo-accordion-item-${index}`}
              className={styles.item}
              icon="plus"
              size="small"
              isOpen={item.openByDefault}
            >
              <SectionAccordion.Header
                className={styles.headerContent}
                title={
                  <h2 className={styles.heading}>
                    <ReactMarkdown
                      remarkPlugins={[gfm]}
                      components={inlineMarkdownComponents}
                    >
                      {item.heading}
                    </ReactMarkdown>
                  </h2>
                }
              />
              <SectionAccordion.Collapsible>
                <MarkdownCopy text={item.body} className={styles.body} />
              </SectionAccordion.Collapsible>
            </SectionAccordion>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default SeoContentAccordion;
